import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Pause, Percent, Gift, X, Anchor, Loader2 } from "lucide-react";

const CANCEL_REASONS = [
    { value: "too_expensive",   label: "Too expensive" },
    { value: "not_using",       label: "Not using it enough" },
    { value: "missing_feature", label: "Missing a feature I need" },
    { value: "found_alternative", label: "Found a better alternative" },
    { value: "seasonal",        label: "Seasonal — back next spring" },
    { value: "sold_boat",       label: "Sold my boat" },
    { value: "other",           label: "Something else" },
];

/**
 * Retention-first cancellation modal.
 *
 * Shows 3 offers (Pause, Discount, Gift Month) before letting the user actually
 * cancel. Each offer is locked once used (eligibility comes from the backend).
 *
 * Props:
 *   open       - boolean
 *   onClose    - fn
 *   onCanceled - fn called after a successful cancellation
 *   onStayed   - fn called when the user accepts a retention offer
 */
export default function CancellationModal({ open, onClose, onCanceled, onStayed }) {
    const [eligibility, setEligibility] = useState(null);
    const [working, setWorking] = useState(null);   // "pause" | "discount" | "gift" | "cancel"
    const [error, setError] = useState("");
    const [view, setView] = useState("offers");     // "offers" | "cancel-form" | "done"
    const [doneCopy, setDoneCopy] = useState("");

    // cancel form
    const [reason, setReason] = useState("");
    const [feedback, setFeedback] = useState("");
    const [pauseMonths, setPauseMonths] = useState(2);
    const dialogRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        setError("");
        setView("offers");
        setReason("");
        setFeedback("");
        api.get("/billing/retention/status")
            .then((r) => setEligibility(r.data))
            .catch(() => setEligibility({ can_pause: true, can_discount: true, can_gift: true, offers_used: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Close on Escape
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;

    const acceptOffer = async (kind) => {
        setError("");
        setWorking(kind);
        try {
            if (kind === "pause") {
                const { data } = await api.post("/billing/retention/pause", { months: pauseMonths });
                setDoneCopy(`Paused — resumes on ${data.paused_until?.split("T")[0]}.`);
            } else if (kind === "discount") {
                await api.post("/billing/retention/discount", {});
                setDoneCopy("50% off applied to your next three billing cycles.");
            } else if (kind === "gift") {
                await api.post("/billing/retention/gift_month", {});
                setDoneCopy("Your next month is on the house.");
            }
            setView("done");
            onStayed?.(kind);
        } catch (e) {
            setError(formatApiErrorDetail(e?.response?.data?.detail) || "Something went wrong. Please try again.");
        } finally {
            setWorking(null);
        }
    };

    const submitCancel = async () => {
        if (!reason) { setError("Pick a reason so we can keep getting better."); return; }
        setError("");
        setWorking("cancel");
        try {
            const { data } = await api.post("/billing/retention/cancel",
                { reason, feedback: feedback.trim() || undefined });
            setDoneCopy(`Your access remains until ${(data.ends_at || "").split("T")[0] || "the end of your billing period"}.`);
            setView("done");
            onCanceled?.(data);
        } catch (e) {
            setError(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't cancel — please try again.");
        } finally {
            setWorking(null);
        }
    };

    const PauseCard = ({ disabled }) => (
        <div className={`bm-cancel-card ${disabled ? "is-disabled" : ""}`} data-testid="cancel-offer-pause">
            <div className="bm-cancel-card-icon"><Pause size={24} /></div>
            <h3 className="bm-cancel-card-title">Pause your subscription</h3>
            <p className="bm-cancel-card-desc">
                Step off for a month or two. Your data stays put, and we resume billing automatically.
            </p>
            <div className="bm-cancel-pause-picker">
                {[1, 2, 3].map((m) => (
                    <button
                        key={m}
                        type="button"
                        disabled={disabled || !!working}
                        className={`bm-cancel-month-btn ${pauseMonths === m ? "is-active" : ""}`}
                        onClick={() => setPauseMonths(m)}
                        data-testid={`cancel-pause-${m}m`}
                    >
                        {m}&nbsp;mo
                    </button>
                ))}
            </div>
            <button
                type="button"
                className="bm-cancel-cta"
                disabled={disabled || !!working}
                onClick={() => acceptOffer("pause")}
                data-testid="cancel-pause-confirm"
            >
                {working === "pause" ? <Loader2 size={16} className="bm-spin" /> : `Pause for ${pauseMonths} month${pauseMonths > 1 ? "s" : ""}`}
            </button>
            {disabled && <div className="bm-cancel-card-lock">Already used once</div>}
        </div>
    );

    const DiscountCard = ({ disabled }) => (
        <div className={`bm-cancel-card ${disabled ? "is-disabled" : ""}`} data-testid="cancel-offer-discount">
            <div className="bm-cancel-card-icon"><Percent size={24} /></div>
            <h3 className="bm-cancel-card-title">50% off, 3 months</h3>
            <p className="bm-cancel-card-desc">
                Half-price for your next three billing cycles. No code needed — applied automatically.
            </p>
            <div className="bm-cancel-card-spacer" />
            <button
                type="button"
                className="bm-cancel-cta"
                disabled={disabled || !!working}
                onClick={() => acceptOffer("discount")}
                data-testid="cancel-discount-confirm"
            >
                {working === "discount" ? <Loader2 size={16} className="bm-spin" /> : "Take 50% off"}
            </button>
            {disabled && <div className="bm-cancel-card-lock">Already used once</div>}
        </div>
    );

    const GiftCard = ({ disabled }) => (
        <div className={`bm-cancel-card ${disabled ? "is-disabled" : ""}`} data-testid="cancel-offer-gift">
            <div className="bm-cancel-card-icon"><Gift size={24} /></div>
            <h3 className="bm-cancel-card-title">A free month, on us</h3>
            <p className="bm-cancel-card-desc">
                Your next billing cycle goes to zero. Use the time to decide if Bosun's Mate is worth it.
            </p>
            <div className="bm-cancel-card-spacer" />
            <button
                type="button"
                className="bm-cancel-cta"
                disabled={disabled || !!working}
                onClick={() => acceptOffer("gift")}
                data-testid="cancel-gift-confirm"
            >
                {working === "gift" ? <Loader2 size={16} className="bm-spin" /> : "Claim my free month"}
            </button>
            {disabled && <div className="bm-cancel-card-lock">Already used once</div>}
        </div>
    );

    return (
        <div
            className="bm-cancel-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancel-modal-title"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            data-testid="cancellation-modal"
        >
            <div className="bm-cancel-dialog" ref={dialogRef} onClick={(e) => e.stopPropagation()}>
                <button
                    type="button"
                    className="bm-cancel-close"
                    onClick={onClose}
                    aria-label="Close"
                    data-testid="cancel-modal-close"
                >
                    <X size={22} />
                </button>

                {view === "offers" && (
                    <>
                        <div className="bm-cancel-header">
                            <Anchor size={26} style={{ color: "var(--bm-gold)" }} />
                            <h2 id="cancel-modal-title" className="bm-serif">
                                Before you go — would any of these help?
                            </h2>
                            <p className="bm-cancel-subhead">
                                I built Bosun's Mate solo, and you sticking around makes it better for the next captain too.
                                Pick whatever feels right — your subscription will still be there if none of these land.
                            </p>
                        </div>

                        {error && <div className="bm-cancel-error" data-testid="cancel-error">{error}</div>}

                        <div className="bm-cancel-grid">
                            <PauseCard    disabled={eligibility && !eligibility.can_pause} />
                            <DiscountCard disabled={eligibility && !eligibility.can_discount} />
                            <GiftCard     disabled={eligibility && !eligibility.can_gift} />
                        </div>

                        <div className="bm-cancel-footer">
                            <button
                                type="button"
                                className="bm-cancel-link-btn"
                                onClick={() => setView("cancel-form")}
                                data-testid="cancel-proceed-anyway"
                            >
                                None of these — cancel anyway
                            </button>
                        </div>
                    </>
                )}

                {view === "cancel-form" && (
                    <>
                        <div className="bm-cancel-header">
                            <h2 id="cancel-modal-title" className="bm-serif">
                                Help me understand why?
                            </h2>
                            <p className="bm-cancel-subhead">
                                One field, fast. This is genuinely the most useful feedback I get — every reason here shapes what gets built next.
                            </p>
                        </div>

                        {error && <div className="bm-cancel-error" data-testid="cancel-error">{error}</div>}

                        <div className="bm-cancel-form">
                            <label className="bm-cancel-label">Reason</label>
                            <div className="bm-cancel-reasons">
                                {CANCEL_REASONS.map((r) => (
                                    <button
                                        key={r.value}
                                        type="button"
                                        className={`bm-cancel-reason ${reason === r.value ? "is-active" : ""}`}
                                        onClick={() => setReason(r.value)}
                                        data-testid={`cancel-reason-${r.value}`}
                                    >
                                        {r.label}
                                    </button>
                                ))}
                            </div>

                            <label className="bm-cancel-label" style={{ marginTop: 18 }}>
                                Anything else? (optional)
                            </label>
                            <textarea
                                value={feedback}
                                onChange={(e) => setFeedback(e.target.value)}
                                placeholder="A feature missing. Something broken. Or just a note for the captain."
                                rows={4}
                                className="bm-cancel-textarea"
                                data-testid="cancel-feedback"
                                maxLength={2000}
                            />
                        </div>

                        <div className="bm-cancel-form-actions">
                            <button
                                type="button"
                                className="bm-cancel-link-btn"
                                onClick={() => setView("offers")}
                                data-testid="cancel-back-to-offers"
                            >
                                ← Back to offers
                            </button>
                            <button
                                type="button"
                                className="bm-cancel-danger-btn"
                                onClick={submitCancel}
                                disabled={!reason || !!working}
                                data-testid="cancel-final-submit"
                            >
                                {working === "cancel" ? <Loader2 size={16} className="bm-spin" /> : "Cancel my subscription"}
                            </button>
                        </div>
                    </>
                )}

                {view === "done" && (
                    <div className="bm-cancel-done" data-testid="cancel-done">
                        <Anchor size={32} style={{ color: "var(--bm-gold)" }} />
                        <h2 className="bm-serif">Done.</h2>
                        <p className="bm-cancel-subhead">{doneCopy}</p>
                        <p className="bm-cancel-subhead" style={{ marginTop: 12 }}>
                            Look out for a confirmation email in the next few minutes.
                        </p>
                        <button
                            type="button"
                            className="bm-cancel-cta"
                            onClick={onClose}
                            data-testid="cancel-close-done"
                            style={{ marginTop: 22 }}
                        >
                            Back to my account
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
